import React, { memo, forwardRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { createPopper } from '@popperjs/core';
import { useForwardedRef } from '../../hooks';
import { useOverlay } from '@react-aria/overlays';
import { ThemeProvider, useTheme } from '../ThemeProvider';
const INVERTED_PLACEMENT_MAP = {
    left: 'right',
    right: 'left',
    'top-start': 'top-end',
    'top-end': 'top-start',
    'bottom-start': 'bottom-end',
    'bottom-end': 'bottom-start',
    'left-start': 'right-start',
    'left-end': 'right-end',
    'right-start': 'left-start',
    'right-end': 'left-end',
};
export const Popper = memo(forwardRef(function Popper({ target, children, opened, Component = 'div', onClose, dismissable, placement: initialPlacement = 'bottom', portal, shouldCloseOnBlur, shouldCloseOnInteractOutside, sameWidth = false, autoFlip = true, offset, arrow, preventOverflow, modifiers, ...rest }, ref) {
    var _a;
    const elRef = useForwardedRef(ref);
    const theme = useTheme();
    const placement = theme.dir === 'rtl' ? (_a = INVERTED_PLACEMENT_MAP[initialPlacement]) !== null && _a !== void 0 ? _a : initialPlacement : initialPlacement;
    useLayoutEffect(() => {
        if (!opened || !target.current || !elRef.current)
            return;
        const arrowProps = arrow ? ('current' in arrow ? { ref: arrow } : arrow) : null;
        const defaultModifiers = [
            {
                name: 'offset',
                options: {
                    offset: offset !== null && offset !== void 0 ? offset : [0, 8],
                },
            },
            {
                name: 'sameWidth',
                enabled: sameWidth,
                fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                effect({ state }) {
                    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                },
                phase: 'beforeWrite',
                requires: ['computeStyles'],
            },
            {
                name: 'arrow',
                enabled: !!arrowProps,
                options: {
                    element: arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.ref.current,
                    padding: arrowProps === null || arrowProps === void 0 ? void 0 : arrowProps.padding,
                },
            },
            {
                name: 'preventOverflow',
                enabled: true,
                options: preventOverflow
            },
        ];
        if (!autoFlip) {
            defaultModifiers.push(
            // https://popper.js.org/docs/v2/modifiers/flip/
            {
                name: 'flip',
                enabled: false,
            }, 
            // https://popper.js.org/docs/v2/modifiers/prevent-overflow/
            {
                name: 'preventOverflow',
                enabled: false,
            });
        }
        const popper = createPopper(target.current, elRef.current, {
            placement,
            modifiers: modifiers || defaultModifiers,
        });
        // иногда неверно инициализируется из-за sameWidth
        // и нужно обновить
        popper.forceUpdate();
        let resizeObserver;
        let targetEl = target.current;
        if (typeof ResizeObserver !== 'undefined' && targetEl) {
            resizeObserver = new ResizeObserver(() => {
                popper.update();
            });
            resizeObserver.observe(targetEl);
        }
        return () => {
            popper === null || popper === void 0 ? void 0 : popper.destroy();
            if (targetEl)
                resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.unobserve(targetEl);
        };
    }, [opened, sameWidth, offset, arrow, placement, autoFlip, modifiers]);
    let { overlayProps } = useOverlay({
        onClose,
        isOpen: onClose && opened,
        isDismissable: dismissable !== false,
        shouldCloseOnBlur: shouldCloseOnBlur,
        shouldCloseOnInteractOutside,
    }, elRef);
    if (!opened)
        return null;
    if (portal !== false && typeof window !== 'undefined') {
        return ReactDOM.createPortal(React.createElement(ThemeProvider, { theme: theme },
            React.createElement(Component, { ref: elRef, ...overlayProps, ...rest, style: { zIndex: 1000, ...rest.style } }, children)), document.body);
    }
    return (React.createElement(Component, { ref: elRef, ...overlayProps, ...rest, style: { zIndex: 1, ...rest.style } }, children));
}));
